import type { Nullable } from "models-webgl2";
import { Vec2 } from "utils/math/vec2";
import { Webgl2dRenderer } from "../../../webgl2d-renderer";
import { Webgl2 } from "../../../webgl2";
import { Webgl2Component } from "../../webgl2-component";
import { ImageRectComponent } from "../image-rect-component";
import { ImageAdjustRectComponent } from "../image-adjust-rect-component";
import { CropSelectionComponent } from "./crop-selection-component";
import { CropUnitComponent } from "./crop-unit-component";
import { Mat3 } from "utils/math/mat3";
import { Transform } from "../../transform";

export class CropSelectionBottomRightComponent extends Webgl2Component {
  private _tm3: Mat3;
  private _cropTopLeft: Vec2;
  private _prevTransform: Transform;

  private get _selectedComponent(): Nullable<ImageRectComponent | ImageAdjustRectComponent> {
    const component = this._renderer.selectedComponent;
    return component instanceof ImageRectComponent || component instanceof ImageAdjustRectComponent
      ? component
      : null;
  }

  constructor(
    private readonly _renderer: Webgl2dRenderer,
    private readonly _cropSelectionComponent: CropSelectionComponent,
  ) {
    super();

    const bottomUnit = new CropUnitComponent(new Vec2(18, 4));
    bottomUnit.style.top = "100%";
    bottomUnit.style.translateY = "-100%";
    bottomUnit.style.pointerEvents = "none";
    this.appendChild(bottomUnit);

    const rightUnit = new CropUnitComponent(new Vec2(4, 18));
    rightUnit.style.left = "100%";
    rightUnit.style.translateX = "-100%";
    rightUnit.style.pointerEvents = "none";
    this.appendChild(rightUnit);

    this.transform.setSize(18, 18);
  }

  public onMouseDown() {
    if (!this._selectedComponent) return;

    const selectedTopLeft = Webgl2dRenderer.global.getComponentClipSpaceInGlobal(
      this._selectedComponent,
      Vec2.zero(),
    );
    const cropTopLeft = Webgl2dRenderer.global.getComponentClipSpaceInGlobal(
      this._cropSelectionComponent,
      Vec2.zero(),
    );
    this._tm3 = Mat3.identity()
      .rotate(-this._selectedComponent.transform.rads)
      .translate(selectedTopLeft.clone().negate());
    this._cropTopLeft = cropTopLeft.transformMat3(this._tm3.mat);
    this._prevTransform = this._cropSelectionComponent.transform.clone();
  }

  public onMouseMove(e: MouseEvent | TouchEvent) {
    if (!this._selectedComponent) return;

    const currentMousePos = this._renderer.camera
      .getGlobalMousePixelPosition(e)
      .transformMat3(this._tm3.mat)
      .clamp(this._cropTopLeft.clone().add(18, 18), new Vec2(Infinity, Infinity));

    const ratio = currentMousePos.distanceTo(this._cropTopLeft) / this._prevTransform.size.length();
    const newSize = this._prevTransform.size
      .clone()
      .multiply(ratio)
      .ratioClamp(
        Vec2.zero(),
        this._selectedComponent.transform.size.clone().sub(this._cropTopLeft),
      );
    const newBottomRight = this._cropTopLeft.clone().add(newSize);

    this._cropSelectionComponent.transform.calcDimensions({
      size: newSize,
      position: newBottomRight
        .median(this._cropTopLeft)
        .transformMat3(this._tm3.clone().invert().mat),
    });
    this._renderer.render();
  }

  public getRenderData() {
    const material = Webgl2.materials.transparent;
    return { material };
  }
}

export default CropSelectionBottomRightComponent;
